import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { 
  Loader, 
  FileText, 
  Tag, 
  Briefcase, 
  DollarSign, 
  Download,
  TrendingUp,
  TrendingDown,
  Info,
  BarChart4,
  MessageSquare,
  Star
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Component for insight card
const InsightCard = ({ 
  title, 
  icon: Icon, 
  description, 
  children, 
  className,
  delay = 0
}: { 
  title: string; 
  icon: any; 
  description?: string;
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
    className={cn(
      "bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 shadow-sm overflow-hidden",
      className
    )}
  >
    <div className="bg-gradient-to-r from-zinc-50 to-zinc-100 dark:from-zinc-900 dark:to-zinc-900 px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-blue-600 dark:text-blue-500" />
          <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">{title}</h3>
        </div>
        {description && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-300">
                  <Info className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-sm">{description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
    <div className="p-6">
      {children}
    </div>
  </motion.div>
);

// Component for empty state
const EmptyState = ({ message }: { message: string }) => (
  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
    <div className="bg-zinc-50 dark:bg-zinc-800/50 p-4 rounded-full mb-4">
      <FileText className="h-8 w-8 text-zinc-400" />
    </div>
    <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">No Data Available</h3>
    <p className="text-sm text-zinc-500 dark:text-zinc-400 max-w-md mb-6">
      {message}
    </p>
  </div>
);

// Component for key metrics
const KeyMetric = ({ label, value, change, icon: Icon }: { label: string; value: string; change?: number; icon: any }) => (
  <div className="bg-white dark:bg-zinc-800 rounded-lg border border-zinc-200 dark:border-zinc-700 shadow-sm p-4">
    <div className="flex items-center gap-3">
      <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-md">
        <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
      </div>
      <div>
        <div className="text-sm text-zinc-500 dark:text-zinc-400 mb-1">{label}</div>
        <div className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">{value}</div>
        {change !== undefined && (
          <div className={`text-sm flex items-center mt-1 ${
            change > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
          }`}>
            {change > 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
            {Math.abs(change)}%
          </div>
        )}
      </div>
    </div>
  </div>
);

// Define interfaces for analysis results
interface FeatureItem {
  name: string;
  items: string[];
}

interface PricingPlan {
  plan: string;
  price: string;
  features?: string[];
}

interface BrandPositioningItem {
  aspect: string;
  description: string;
}

interface MarketPositioning {
  value_proposition?: string;
  price_point?: string;
  market_segment?: string;
}

interface CompetitiveLandscape {
  differentiators?: { item?: string[] };
  advantages?: { item?: string[] };
  challenges?: { item?: string[] };
}

interface TargetMarket {
  primary_audience?: string;
  secondary_audience?: string;
  industry_focus?: { industry?: string[] };
}

interface MarketAnalysis {
  positioning?: MarketPositioning;
  competitive_landscape?: CompetitiveLandscape;
  target_market?: TargetMarket;
}

// Database analysis results structure (matching what's actually in the DB)
interface AnalysisResults {
  description?: string;
  targetAudience?: Array<{ segment: string; description: string }>;
  features?: FeatureItem[];
  pricing?: PricingPlan[];
  brandPositioning?: BrandPositioningItem[];
  painPoints?: Array<{ problem: string }>;
  desires?: Array<{ desire: string }>;
  socialProof?: Array<{ testimony: string; source: string }>;
  keywords?: {
    core?: string[];
    competitive?: string[];
    niche?: string[];
    longTail?: string[];
  };
  market_analysis?: MarketAnalysis;
  [key: string]: any; // Allow for other properties we might not know about
}

// Transformed results with better structure for UI rendering
interface TransformedResults {
  description?: string;
  features?: FeatureItem[];
  pricing?: PricingPlan[];
  brandPositioning?: BrandPositioningItem[];
  marketAnalysis?: {
    positioning: {
      valueProposition: string;
      pricePoint: string;
      marketSegment: string;
    };
    competitiveLandscape: {
      differentiators: string[];
      advantages: string[];
      challenges: string[];
    };
    targetMarket: {
      primaryAudience: string;
      secondaryAudience: string;
      industryFocus: string[];
    };
  } | null;
}

// Default empty state structure to ensure components always have valid data to work with
const DEFAULT_RESULTS: TransformedResults = {
  description: '',
  features: [],
  pricing: [],
  brandPositioning: [],
  marketAnalysis: {
    positioning: {
      valueProposition: '',
      pricePoint: '',
      marketSegment: ''
    },
    competitiveLandscape: {
      differentiators: [],
      advantages: [],
      challenges: []
    },
    targetMarket: {
      primaryAudience: '',
      secondaryAudience: '',
      industryFocus: []
    }
  }
};

const ProductPage = () => {
  // Initialize with default structure instead of null
  const [results, setResults] = useState<TransformedResults>(DEFAULT_RESULTS);
  const [isLoading, setIsLoading] = useState(true);
  const { id: projectId } = useParams();
  const { toast } = useToast();

  useEffect(() => {
    const fetchResults = async () => {
      if (!projectId) return;
      setIsLoading(true);

      try {
        // First, check if the user is authenticated
        const { data: userData, error: userError } = await supabase.auth.getUser();
        
        if (userError) {
          console.error('Auth error:', userError);
          throw userError;
        }
        
        if (!userData.user) {
          console.error('No authenticated user found');
          throw new Error('No authenticated user found');
        }
        
        console.log('Fetching analysis for project:', projectId);
        
        // Use a more specific query to get just what we need
        const { data: crawl, error } = await supabase
          .from('website_crawls')
          .select('id, project_id, analysis_results, status, created_at')
          .eq('project_id', projectId)
          .eq('status', 'completed')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error('Error fetching results:', error);
          throw error;
        }

        console.log('Raw crawl data:', crawl);
        
        // If no data found, handle gracefully
        if (!crawl) {
          console.log('No crawl data found - ensure project ID is correct');
          // Set default structure instead of null
          setResults(DEFAULT_RESULTS);
          setIsLoading(false);
          return;
        }
        
        // Check if analysis_results exist
        if (!crawl.analysis_results) {
          console.log('No analysis_results found in the crawl data');
          // Set default structure instead of null
          setResults(DEFAULT_RESULTS);
          setIsLoading(false);
          return;
        }
        
        console.log('Analysis results type:', typeof crawl.analysis_results);
        
        // Safe type casting - ensure we handle both string (JSON) and object formats
        let analysisData: AnalysisResults;
        
        try {
          // If the analysis_results is a string (happens in some DB setups), parse it
          if (typeof crawl.analysis_results === 'string') {
            analysisData = JSON.parse(crawl.analysis_results) as AnalysisResults;
            console.log('Successfully parsed string analysis_results');
          } else {
            // Otherwise, use it directly as an object
            analysisData = crawl.analysis_results as AnalysisResults;
            console.log('Using object analysis_results directly');
          }
          
          // Additional structure validation and logging
          console.log('Top-level analysis data keys:', Object.keys(analysisData));
          
          // Clean XML tags from the results
          const cleanXMLTags = (text: string | undefined): string => {
            if (!text) return '';
            return text.replace(/<\/?[^>]+(>|$)/g, "");
          };

          const cleanArrayXMLTags = (arr: string[] | undefined): string[] => {
            if (!arr || !Array.isArray(arr)) return [];
            return arr.map(item => cleanXMLTags(item)).filter(Boolean);
          };

          // Helper function to ensure we get a valid array
          const ensureArray = <T,>(maybeArray: T[] | undefined | null, defaultItem?: T): T[] => {
            if (!maybeArray || !Array.isArray(maybeArray)) return defaultItem ? [defaultItem] : [];
            return maybeArray;
          };

          // Transform the data with safe accessors and robust null checks
          const cleanedResults: TransformedResults = {
            description: cleanXMLTags(analysisData.description),
            
            // Process features with additional error checking
            features: Array.isArray(analysisData.features)
              ? analysisData.features
                  .filter(f => typeof f === 'object' && f?.name && Array.isArray(f?.items))
                  .map(f => ({
                    name: cleanXMLTags(f.name) || '',
                    items: cleanArrayXMLTags(f.items) || []
                  }))
              : [],
            
            // Process pricing
            pricing: Array.isArray(analysisData.pricing)
              ? analysisData.pricing
                  .filter(p => p && typeof p === 'object')
                  .map(p => ({
                    ...p,
                    plan: p.plan || 'Unnamed Plan',
                    price: p.price || 'Contact for pricing',
                    features: cleanArrayXMLTags(p.features) || []
                  }))
              : [],
            
            // Process brand positioning
            brandPositioning: Array.isArray(analysisData.brandPositioning)
              ? analysisData.brandPositioning
                  .filter(b => b && typeof b === 'object')
                  .map(b => ({
                    aspect: cleanXMLTags(b.aspect) || '',
                    description: cleanXMLTags(b.description) || ''
                  }))
                  .filter(b => b.aspect && b.description)
              : [],
            
            // Process market analysis with defensive coding - always provide a structure instead of null
            marketAnalysis: {
              positioning: {
                valueProposition: cleanXMLTags(analysisData.market_analysis?.positioning?.value_proposition) || 'Value-focused solution',
                pricePoint: cleanXMLTags(analysisData.market_analysis?.positioning?.price_point) || 'Competitive',
                marketSegment: cleanXMLTags(analysisData.market_analysis?.positioning?.market_segment) || 'General business'
              },
              competitiveLandscape: {
                differentiators: cleanArrayXMLTags(analysisData.market_analysis?.competitive_landscape?.differentiators?.item) || [],
                advantages: cleanArrayXMLTags(analysisData.market_analysis?.competitive_landscape?.advantages?.item) || [],
                challenges: cleanArrayXMLTags(analysisData.market_analysis?.competitive_landscape?.challenges?.item) || []
              },
              targetMarket: {
                primaryAudience: cleanXMLTags(analysisData.market_analysis?.target_market?.primary_audience) || 'Business professionals',
                secondaryAudience: cleanXMLTags(analysisData.market_analysis?.target_market?.secondary_audience) || 'Small businesses',
                industryFocus: cleanArrayXMLTags(analysisData.market_analysis?.target_market?.industry_focus?.industry) || []
              }
            }
          };
          
          console.log('Cleaned product analysis results:', cleanedResults);
          setResults(cleanedResults);
        } catch (parseError) {
          console.error('Error parsing or processing analysis results:', parseError);
          toast({
            title: "Data Processing Error",
            description: "There was an error processing the analysis data. Please try again or contact support.",
            variant: "destructive",
          });
          // Set default structure instead of null
          setResults(DEFAULT_RESULTS);
        }
      } catch (error) {
        console.error('Error fetching results:', error);
        toast({
          title: "Error",
          description: "Failed to fetch research results: " + (error.message || "Unknown error"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchResults();
  }, [projectId, toast]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8 h-[calc(100vh-200px)]">
        <div className="animate-spin">
          <Loader className="h-8 w-8 text-blue-600" />
        </div>
      </div>
    );
  }

  // Check if we have any meaningful data across all sections
  const hasAnyData = (
    (results.description && results.description.trim().length > 0) ||
    (results.features && results.features.length > 0) ||
    (results.pricing && results.pricing.length > 0) ||
    (results.brandPositioning && results.brandPositioning.length > 0) ||
    (results.marketAnalysis && (
      results.marketAnalysis.competitiveLandscape.differentiators.length > 0 ||
      results.marketAnalysis.competitiveLandscape.advantages.length > 0 ||
      results.marketAnalysis.competitiveLandscape.challenges.length > 0 ||
      results.marketAnalysis.targetMarket.industryFocus.length > 0 ||
      (results.marketAnalysis.positioning.valueProposition && 
        results.marketAnalysis.positioning.valueProposition !== 'Value-focused solution') ||
      (results.marketAnalysis.targetMarket.primaryAudience && 
        results.marketAnalysis.targetMarket.primaryAudience !== 'Business professionals')
    ))
  );

  if (!hasAnyData) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-950/20 dark:to-blue-950/20 rounded-xl p-8 shadow-sm border border-blue-100 dark:border-blue-900/50">
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-blue-400 mx-auto mb-4 opacity-50" />
            <h2 className="text-2xl font-bold text-blue-600 mb-2">No Product Analysis Available</h2>
            <p className="text-zinc-600 dark:text-zinc-400 max-w-md mx-auto">
              Product analysis data is not available yet. Please analyze a website first to see product details.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Ensure we have valid arrays for display, never undefined
  const featureRows = results.features?.filter(Boolean).map(feature => ({
    name: feature.name,
    items: feature.items?.filter(Boolean) || []
  })) || [];

  // Calculate some metrics for the summary section
  const featureCount = featureRows.reduce((acc, curr) => acc + (curr.items?.length || 0), 0);
  const pricingTiers = results.pricing?.length || 0;
  const positioningPoints = results.brandPositioning?.length || 0;

  return (
    <div className="flex flex-col min-h-screen bg-zinc-50 dark:bg-zinc-900/50 animate-fadeIn">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-zinc-50 to-zinc-100 dark:from-zinc-900 dark:to-zinc-900 border-b border-zinc-200 dark:border-zinc-800">
        <div className="container mx-auto py-8 px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight">Product Analysis</h1>
              <p className="text-zinc-500 dark:text-zinc-400 mt-1">Detailed analysis of the product's features, pricing, and positioning</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 container mx-auto py-6 px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Primary Insights Column - 2/3 width on large screens */}
          <div className="lg:col-span-2 space-y-6">
            {/* Product Description */}
            <InsightCard 
              title="Product Description" 
              icon={FileText}
              description="Overview of the product's main purpose and value proposition"
            >
              {results.description ? (
                <div className="prose dark:prose-invert max-w-none">
                  <p className="text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap leading-relaxed">{results.description}</p>
                </div>
              ) : (
                <EmptyState message="No product description available." />
              )}
            </InsightCard>
            
            {/* Product Features */}
            <InsightCard 
              title="Product Features" 
              icon={Briefcase}
              description="Key features and capabilities of the product"
              delay={0.1}
            >
              {featureRows.length > 0 ? (
                <div className="grid gap-6 md:grid-cols-2">
                  {featureRows.filter(category => category.name && category.items?.length > 0).map((category, index) => (
                    <motion.div 
                      key={index} 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.1 + (index * 0.05) }}
                      className="bg-zinc-50 dark:bg-zinc-800/50 rounded-lg p-5 border border-zinc-200 dark:border-zinc-700"
                    >
                      <h3 className="font-medium text-lg text-blue-600 dark:text-blue-400 mb-3">{category.name}</h3>
                      <ul className="space-y-2">
                        {category.items.map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <div className="bg-blue-100 dark:bg-blue-900/30 rounded-md p-1 mt-0.5 flex items-center justify-center">
                              <span className="h-1.5 w-1.5 bg-blue-500 rounded-full"></span>
                            </div>
                            <span className="text-zinc-700 dark:text-zinc-300 text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <EmptyState message="No feature information available." />
              )}
            </InsightCard>
            
            {/* Pricing Plans */}
            <InsightCard 
              title="Pricing Plans" 
              icon={DollarSign}
              description="Pricing structure and plan details"
              delay={0.2}
            >
              {results.pricing && results.pricing.length > 0 ? (
                <div className="grid md:grid-cols-3 gap-6">
                  {results.pricing.map((plan, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.2 + (index * 0.05) }}
                      className={cn(
                        "rounded-lg p-6 border shadow-sm",
                        index === 1 ? "border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10" : "bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700"
                      )}
                    >
                      <h3 className={cn(
                        "text-xl font-semibold mb-2", 
                        index === 1 ? "text-blue-600 dark:text-blue-400" : "text-zinc-900 dark:text-zinc-100"
                      )}>
                        {plan.plan}
                      </h3>
                      <div className="text-2xl font-bold mb-4 text-zinc-900 dark:text-zinc-100">{plan.price}</div>
                      <ul className="space-y-2">
                        {plan.features?.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <div className={cn(
                              "rounded-md p-1 mt-0.5 flex items-center justify-center",
                              index === 1 ? "bg-blue-100 dark:bg-blue-900/30" : "bg-zinc-100 dark:bg-zinc-700/30"
                            )}>
                              <span className={cn(
                                "h-1.5 w-1.5 rounded-full",
                                index === 1 ? "bg-blue-500" : "bg-zinc-500"
                              )}></span>
                            </div>
                            <span className="text-zinc-700 dark:text-zinc-300 text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <EmptyState message="No pricing information available." />
              )}
            </InsightCard>
          </div>
          
          {/* Secondary Insights Column - 1/3 width on large screens */}
          <div className="space-y-6">
            {/* Brand Positioning */}
            <InsightCard 
              title="Brand Positioning" 
              icon={BarChart4}
              description="How the product is positioned in the market"
              delay={0.3}
            >
              {results.brandPositioning && results.brandPositioning.length > 0 ? (
                <div className="space-y-4">
                  {results.brandPositioning.map((item, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.3 + (index * 0.05) }}
                      className="rounded-lg p-4 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700"
                    >
                      <h3 className="text-base font-semibold text-blue-600 dark:text-blue-400 mb-2">{item.aspect}</h3>
                      <p className="text-zinc-700 dark:text-zinc-300 text-sm">{item.description}</p>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <EmptyState message="No brand positioning information available." />
              )}
            </InsightCard>
            
            {/* Market Analysis */}
            <InsightCard 
              title="Market Analysis" 
              icon={BarChart4}
              description="Market positioning and competitive landscape"
              delay={0.4}
            >
              {results.marketAnalysis ? (
                <div className="space-y-4">
                  <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-lg p-4 border border-zinc-200 dark:border-zinc-700">
                    <h4 className="font-medium text-zinc-900 dark:text-zinc-100 mb-3">Market Positioning</h4>
                    <p className="text-zinc-700 dark:text-zinc-300 text-sm mb-3">
                      Based on website content analysis, this product appears to be positioned as a 
                      {results.marketAnalysis.positioning.valueProposition ? (
                        <span className="inline-flex flex-wrap gap-1.5 mx-1.5">
                          {results.marketAnalysis.positioning.valueProposition.split(',').map((tag, idx) => (
                            <Badge key={idx} className="bg-blue-50 text-blue-700 border-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800/30">
                              {tag.trim()}
                            </Badge>
                          ))}
                        </span>
                      ) : (
                        <Badge className="mx-1.5 bg-blue-50 text-blue-700 border-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800/30">
                          premium solution
                        </Badge>
                      )}
                      in its category, emphasizing quality and comprehensive features over low cost.
                    </p>
                    <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700">
                      <div className="grid grid-cols-3 gap-2 text-center text-xs">
                        <div className="p-2 rounded-md bg-zinc-100 dark:bg-zinc-800">
                          <div className="font-medium text-zinc-900 dark:text-zinc-100 mb-1">Value Proposition</div>
                          {results.marketAnalysis.positioning.valueProposition ? (
                            <ul className="text-zinc-500 dark:text-zinc-400 text-left pl-2 space-y-1">
                              {results.marketAnalysis.positioning.valueProposition.split(',').map((item, idx) => (
                                <li key={idx} className="flex items-start">
                                  <span className="inline-block h-1 w-1 bg-zinc-400 dark:bg-zinc-600 rounded-full mr-1.5 mt-1.5"></span>
                                  <span>{item.trim()}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div className="text-zinc-500 dark:text-zinc-400">Quality-focused</div>
                          )}
                        </div>
                        <div className="p-2 rounded-md bg-zinc-100 dark:bg-zinc-800">
                          <div className="font-medium text-zinc-900 dark:text-zinc-100 mb-1">Price Point</div>
                          <div className="text-zinc-500 dark:text-zinc-400">{results.marketAnalysis.positioning.pricePoint || 'Premium'}</div>
                        </div>
                        <div className="p-2 rounded-md bg-zinc-100 dark:bg-zinc-800">
                          <div className="font-medium text-zinc-900 dark:text-zinc-100 mb-1">Market Segment</div>
                          {results.marketAnalysis.positioning.marketSegment ? (
                            <ul className="text-zinc-500 dark:text-zinc-400 text-left pl-2 space-y-1">
                              {results.marketAnalysis.positioning.marketSegment.split(',').map((item, idx) => (
                                <li key={idx} className="flex items-start">
                                  <span className="inline-block h-1 w-1 bg-zinc-400 dark:bg-zinc-600 rounded-full mr-1.5 mt-1.5"></span>
                                  <span>{item.trim()}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div className="text-zinc-500 dark:text-zinc-400">Enterprise</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-lg p-4 border border-zinc-200 dark:border-zinc-700">
                    <h4 className="font-medium text-zinc-900 dark:text-zinc-100 mb-3">Competitive Landscape</h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 dark:bg-blue-900/30 rounded-md p-1.5 mt-0.5 flex items-center justify-center">
                          <span className="h-1.5 w-1.5 bg-blue-500 rounded-full"></span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Key Differentiators</div>
                          {results.marketAnalysis.competitiveLandscape.differentiators.length > 0 ? (
                            <ul className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 list-inside space-y-1">
                              {results.marketAnalysis.competitiveLandscape.differentiators.map((item, idx) => (
                                <li key={idx} className="flex items-start">
                                  <span className="inline-block h-1 w-1 bg-zinc-400 dark:bg-zinc-600 rounded-full mr-1.5 mt-1.5"></span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-xs text-zinc-500 dark:text-zinc-400">
                              Comprehensive feature set, intuitive interface, and dedicated support
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 dark:bg-blue-900/30 rounded-md p-1.5 mt-0.5 flex items-center justify-center">
                          <span className="h-1.5 w-1.5 bg-blue-500 rounded-full"></span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Competitive Advantages</div>
                          {results.marketAnalysis.competitiveLandscape.advantages.length > 0 ? (
                            <ul className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 list-inside space-y-1">
                              {results.marketAnalysis.competitiveLandscape.advantages.map((item, idx) => (
                                <li key={idx} className="flex items-start">
                                  <span className="inline-block h-1 w-1 bg-zinc-400 dark:bg-zinc-600 rounded-full mr-1.5 mt-1.5"></span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-xs text-zinc-500 dark:text-zinc-400">
                              Specialized industry knowledge, advanced technology, and established market presence
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 dark:bg-blue-900/30 rounded-md p-1.5 mt-0.5 flex items-center justify-center">
                          <span className="h-1.5 w-1.5 bg-blue-500 rounded-full"></span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Market Challenges</div>
                          {results.marketAnalysis.competitiveLandscape.challenges.length > 0 ? (
                            <ul className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 list-inside space-y-1">
                              {results.marketAnalysis.competitiveLandscape.challenges.map((item, idx) => (
                                <li key={idx} className="flex items-start">
                                  <span className="inline-block h-1 w-1 bg-zinc-400 dark:bg-zinc-600 rounded-full mr-1.5 mt-1.5"></span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-xs text-zinc-500 dark:text-zinc-400">
                              Emerging competitors with lower price points and rapid feature development
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-lg p-4 border border-zinc-200 dark:border-zinc-700">
                    <h4 className="font-medium text-zinc-900 dark:text-zinc-100 mb-3">Target Market</h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 dark:bg-blue-900/30 rounded-md p-1.5 mt-0.5 flex items-center justify-center">
                          <span className="h-1.5 w-1.5 bg-blue-500 rounded-full"></span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Primary Audience</div>
                          <p className="text-xs text-zinc-500 dark:text-zinc-400">
                            {results.marketAnalysis.targetMarket.primaryAudience || 'Enterprise businesses seeking comprehensive solutions'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 dark:bg-blue-900/30 rounded-md p-1.5 mt-0.5 flex items-center justify-center">
                          <span className="h-1.5 w-1.5 bg-blue-500 rounded-full"></span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Secondary Audience</div>
                          <p className="text-xs text-zinc-500 dark:text-zinc-400">
                            {results.marketAnalysis.targetMarket.secondaryAudience || 'Mid-market companies looking to scale operations'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 dark:bg-blue-900/30 rounded-md p-1.5 mt-0.5 flex items-center justify-center">
                          <span className="h-1.5 w-1.5 bg-blue-500 rounded-full"></span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Industry Focus</div>
                          {results.marketAnalysis.targetMarket.industryFocus.length > 0 ? (
                            <ul className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 list-inside space-y-1">
                              {results.marketAnalysis.targetMarket.industryFocus.map((item, idx) => (
                                <li key={idx} className="flex items-start">
                                  <span className="inline-block h-1 w-1 bg-zinc-400 dark:bg-zinc-600 rounded-full mr-1.5 mt-1.5"></span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-xs text-zinc-500 dark:text-zinc-400">
                              Technology, finance, healthcare, and professional services
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <EmptyState message="No market analysis information available." />
              )}
            </InsightCard>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
